"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Quote, Diamond } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const testimonials = [{
  id: 1,
  quote: "Custom RTD Co. didn't just manufacture our cans; they engineered our brand's success. The precision in their counter-pressure canning is nothing short of technical art.",
  name: "Julianna Reed",
  role: "Founder, BioFizz Probiotics",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/julianna_reed.png"
}, {
  id: 2,
  quote: "In a market flooded with inconsistent quality, this facility provides the standard. Their low-MOQ flexibility turned our pilot project into a liquid asset.",
  name: "Marcus Chen",
  role: "Director of Operations, ZenSpark Adaptogens",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/marcus_chen.png"
}, {
  id: 3,
  quote: "The precision with which they handle our carbonation levels is terrifyingly consistent. It’s the kind of technical advantage that feels like a secret weapon.",
  name: "Sarah Vance",
  role: "Head of R&D, HydraPeak Electrolytes",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/sarah_vance.png"
}, {
  id: 4,
  quote: "We needed a partner, not just a plant. Custom RTD delivered a system so robust it feels like it's from a decade in the future. Absolute elite execution.",
  name: "David Miller",
  role: "Creative Director, Lumina Wellness",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/david_miller.png"
}];
export default function ClientAlphaResults() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  React.useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return <section data-section-id="6782" id="partner-success-stories" ref={ref} className={cn("dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-secondary/20 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 text-primary tracking-widest uppercase text-xs font-mono font-medium">
            <Diamond className="w-3 h-3 fill-current" />
            <span>Technical Excellence</span>
            <Diamond className="w-3 h-3 fill-current" />
          </div>
          <h2 className="text-3xl md:text-5xl font-sans font-bold tracking-tighter text-foreground">
            The <span className="text-primary">Precision</span> Standard
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-light">
            Elite beverage brands that have scaled their vision through our technical manufacturing infrastructure.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {testimonials.map((item, i) => <CarouselItem key={item.id} data-index={i} className="pl-4 md:basis-2/3 lg:basis-1/2">
                  <div className="h-full p-2">
                    <Card className="group relative h-full border-0 bg-transparent shadow-none">
                      <div className="absolute inset-0 bg-card/40 backdrop-blur-md border border-white/10 transition-all duration-500 group-hover:border-primary/50 group-hover:bg-card/60" style={{
                    clipPath: "polygon(0 0, calc(100% - 40px) 0, 100% 40px, 100% 100%, 0 100%)"
                  }} />
                      
                      <div className="absolute top-0 right-0 w-[40px] h-[40px] pointer-events-none overflow-hidden">
                         <div className="absolute top-0 right-0 w-[56px] h-[1px] bg-primary/40 origin-top-right -rotate-45 translate-y-[20px] translate-x-[-12px]" />
                         <div className="absolute top-[10px] right-[10px] w-0 h-0 border-l-[10px] border-l-transparent border-b-[10px] border-b-primary/20 border-r-[10px] border-r-transparent rotate-45" />
                      </div>

                      <CardContent className="relative p-8 md:p-12 flex flex-col items-center text-center h-full justify-center">
                        <div className="absolute top-8 left-8 text-primary/20">
                          <Quote className="w-8 h-8 rotate-180" />
                        </div>

                        <div className="mb-8 relative">
                          <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
                          <SafeHydrate><Avatar className="w-24 h-24 border-2 border-primary/10 group-hover:border-primary transition-colors duration-500">
                            <AvatarImage src={item.image} alt={item.name} className="grayscale group-hover:grayscale-0 transition-all duration-700 object-cover" />
                            <AvatarFallback className="bg-muted text-muted-foreground text-xl">
                              {item.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div className="absolute -bottom-1 -right-1 w-0 h-0 border-l-[8px] border-l-transparent border-b-[8px] border-b-primary border-r-[8px] border-r-transparent rotate-[-45deg]" />
                        </div>

                        <blockquote className="font-sans text-xl md:text-2xl leading-relaxed text-foreground/90 mb-8 italic tracking-tight">
                          "{item.quote}"
                        </blockquote>

                        <div className="mt-auto space-y-1">
                          <div className="font-sans font-bold text-primary tracking-wide uppercase text-sm">
                            {item.name}
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground tracking-widest uppercase">
                            {item.role}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            <div className="hidden md:block">
              <CarouselPrevious className="h-14 w-14 rounded-none rotate-45 border border-white/10 bg-background/50 backdrop-blur-md text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 [&_svg]:-rotate-45 -left-20" />
              <CarouselNext className="h-14 w-14 rounded-none rotate-45 border border-white/10 bg-background/50 backdrop-blur-md text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 [&_svg]:-rotate-45 -right-20" />
            </div>
            
            <div className="flex justify-center gap-2 mt-8 md:hidden">
                {testimonials.map((_, i) => <button key={i} onClick={() => api?.scrollTo(i)} className={cn("w-2 h-2 rotate-45 transition-all duration-300", current === i ? "bg-primary scale-125" : "bg-muted-foreground/30")} aria-label={`Go to slide ${i + 1}`} />)}
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}